import { getContentInfo } from '../smadi/smadi'
import * as Logger from '@mamdev/navigator.frontend-logger'
import { connectToChild, Connection, CallSender } from 'penpal'
import { getTimeout } from '../../../util/timeout'
import { monitoringService } from '../monitoring/monitoring.service'
import { getCacheBuster } from '../../../util/cache-buster'
import { AditionNextData, UppContentMethods } from '../../../content/interfaces'
import { AditionIframe, ContentInfo, UppClientOptions } from '../../interfaces'
import { getProfileParams } from '../smadi/profile-params'
import {
  ContentMessage,
  ContentParameters,
  PublisherMessage,
  UppContentMetaData,
  UppContentOptions
} from '../../../interfaces'
import { getForcedContentData } from '../../../util/get-forced-content-data'
import { getReasonFromError } from '../../../util/reason-from-error'

export type Resolver = {
  resolve: (value?: unknown) => void
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  reject: (reason?: any) => void
}

export const IFRAME_ID = '__upp-content__'

export class ContentService {
  moduleName = 'ContentService'

  private iframe: HTMLIFrameElement
  private windowId: string = getCacheBuster() // Needs to be the same through the lifetime of a UPP instance!
  private childConnection: Connection<CallSender>
  private clientOptions: UppClientOptions
  private contentOptions: UppContentOptions[] = []
  private contentInfo: ContentInfo = null
  private lifeCyclePromiseResolver: Resolver

  /**
   * true -> end the life cycle after the currently displayed item calls next()
   */
  private completeAfterNext = false

  private childApi: Partial<UppContentMethods> = {
    connect: this.connect.bind(this),
    getParameters: this.getParameters.bind(this),
    sendMessage: this.sendMessage.bind(this),
    done: this.done.bind(this),
  }

  public async init(clientConfig: UppClientOptions): Promise<void> {
    this.clientOptions = clientConfig;

    // clean up any iframe from a previous invocation
    const container = await this.getContentContainer()
    container?.querySelector(`#${IFRAME_ID}`)?.remove()

    const lifeCyclePromise = new Promise<void>((resolve, reject) => {
      this.lifeCyclePromiseResolver = {
        resolve,
        reject
      }
    })

    try {
      await this.loadContent()
    } catch (e) {
      this.exit(e)
    }

    return lifeCyclePromise
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async loadContent(nextData: AditionNextData = {}) {
    const aditionParams = {
      ...getProfileParams(nextData, 'nd'),
      ...getProfileParams({
        items_displayed: this.contentOptions.map(options => options?.name),
        items_count: this.contentOptions.length.toString()
      }, 'hist')
    }

    if (typeof this.clientOptions?.getContentUrl === 'function') {
      this.contentInfo = {
        content_url: await this.clientOptions.getContentUrl()
      }
      Logger.debug(this.moduleName, 'loadContent', `Received content URL from custom getContentUrl function: ${this.contentInfo?.content_url}`)
    } else {
      this.contentInfo = await this.getContentInfoFromSmAdi(aditionParams)
      Logger.debug(this.moduleName, 'loadContent', `Received content URL from SmAdi: ${this.contentInfo?.content_url}`)
    }

    if (!this.contentInfo?.content_url) {
      this.exit()
      return
    }

    this.prepareIframe()
    this.iframe.src = this.contentInfo.content_url;
    (await this.getContentContainer()).appendChild(this.iframe)

    const start = Date.now()

    try {
      this.childConnection = connectToChild({
        iframe: this.iframe,
        timeout: getTimeout(this.clientOptions),
        methods: this.childApi,
      })

      await this.childConnection.promise
    } catch (err) {
      err.message = `Error establishing connection to content iframe with URL ${this.contentInfo?.content_url}: ${err.message}`
      throw err
    }

    monitoringService?.monitorUppContentIframeLoadDuration(start)

    Logger.debug(this.moduleName, 'loadAditionFrame', `New content frame created: `, this.contentInfo.content_url)
  }

  public async getContentInfoFromSmAdi(params?: { [key: string]: string }): Promise<ContentInfo | null> {
    const start = Date.now()
    const contentInfo = await getContentInfo(this.clientOptions, params, this.windowId)
    monitoringService?.monitorUppSmAdiRequestDuration(start)
    Logger.debug(this.moduleName, 'getContentUrlFromSmAdi', 'received content info', JSON.stringify(contentInfo, null, 2))

    if (contentInfo?.complete === true) {
      this.completeAfterNext = true
    }

    if (typeof this.clientOptions?.onContentInfoReceived === 'function') {
      try {
        Logger.debug(this.moduleName, 'Calling onContentInfoReceived with contentInfo:', contentInfo)
        await this.clientOptions.onContentInfoReceived(contentInfo)
      } catch (e) {
        Logger.error(`[${this.moduleName}] getContentUrlFromSmAdi: Error while calling onContentInfoReceived callback`, e)
      }
    }

    return contentInfo
  }

  public prepareIframe() {
    if (this.childConnection) {
      try {
        this.childConnection.destroy()
        // eslint-disable-next-line no-empty
      } catch (ex) {}
    }

    // remove the iframe from the DOM if Penpal can't do it
    this.iframe?.remove()

    this.iframe = document.createElement('iframe');
    const iframeAttributes: AditionIframe = {
      id: IFRAME_ID,
      border: '0',
      frameborder: '0',
      marginwidth: '0',
      marginheight: '0',
      allowtransparency: 'true',
      background: 'transparent',
      scrolling: 'yes',
      sandbox: 'allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts',
      style: 'display:block; width: 100%;'
    }

    Object.keys(iframeAttributes).forEach((attr) => {
      this.iframe.setAttribute(attr, iframeAttributes[attr as keyof AditionIframe])
    })

    // no custom container: full screen overlay
    if (!this.clientOptions?.contentContainer) {
      this.iframe.style.position = 'fixed'
      this.iframe.style.top = '0px'
      this.iframe.style.right = '0px'
      this.iframe.style.bottom = '0px'
      this.iframe.style.left = '0px'
      this.iframe.style.height = '100%'
    } else {
      this.iframe.style.height = this.contentInfo?.height || '100%'
    }
  }

  /**
   * Return the parent element for the content iframe
   */
  public async getContentContainer(): Promise<HTMLElement> {
    if (this.clientOptions?.contentContainer instanceof HTMLElement) {
      return this.clientOptions.contentContainer
    }
    if (typeof this.clientOptions?.contentContainer === 'function') {
      return this.clientOptions.contentContainer()
    }

    return document.body
  }

  public async connect(options?: UppContentOptions): Promise<void> {
    Logger.debug(this.moduleName, 'Content called connect', options)
    if (typeof options?.title === 'string') {
      this.iframe?.setAttribute('title', options.title)
    }

    if (typeof this.clientOptions?.onShowContent === 'function') {
      const contentData: UppContentMetaData = {
        ...options,
        urn: this.contentInfo?.content_urn,
      }
      Logger.debug(this.moduleName, 'Calling onShowContent with contentData:', contentData)
      try {
        await this.clientOptions.onShowContent(contentData)
      } catch (e) {
        Logger.error(`[${this.moduleName}] connect: Error while calling onShowContent callback`, e)
      }
    }

    this.contentOptions.push(options)
    monitoringService?.monitorUppContentConnected(options?.name, options?.variant)
  }

  public async done(nextData?: AditionNextData): Promise<void> {
    Logger.debug(this.moduleName, 'Content called next', JSON.stringify(nextData, null, 2))
    const currentContentOptions = this.getCurrentContentOptions()
    monitoringService?.monitorUppContentNextCalled(currentContentOptions.name, currentContentOptions.variant)

    if (this.completeAfterNext) {
      Logger.debug(this.moduleName, 'completeAfterNext: calling complete')
      return this.exit()
    }

    monitoringService?.flush()

    await this.loadContent(nextData)
  }

  public async getParameters(): Promise<ContentParameters> {
    const parameters: ContentParameters = {
      portal: this.clientOptions.portal,
      stage: this.clientOptions.stage,
      category: this.clientOptions.category,
      section: this.clientOptions.section,
      campaign: this.clientOptions.campaign,
      contentGroup: this.clientOptions.contentGroup,
      forceContent: this.clientOptions.forceContent,
      xUIApp: this.clientOptions.xUIApp,
      touchpointName: this.clientOptions.touchpointName,
      touchpointReference: this.clientOptions.touchpointReference,
      ...this.clientOptions.contentParameters,
    }

    // SWAT-7619: Content within the Navigator should always use the navsid (in contentParameters) for auth
    if (typeof this.clientOptions.section === 'string' && !this.clientOptions.section.startsWith('navigator')) {
      parameters.accessToken = this.clientOptions.accessToken
    }

    return parameters
  }

  public async sendMessage(message: ContentMessage): Promise<PublisherMessage> {
    if (typeof this.clientOptions?.onContentMessage === 'function') {
      return this.clientOptions?.onContentMessage(message)
    }
    return null
  }

  public async complete(): Promise<void> {
    if (!this.completeAfterNext) {
      const currentContentOptions = this.getCurrentContentOptions()
      monitoringService?.monitorUppContentCompleteCalled(currentContentOptions.name, currentContentOptions.variant)
    }
    this.exit()
  }

  public getCurrentContentOptions(): UppContentOptions {
    return this.contentOptions[this.contentOptions.length - 1] || {
      name: 'unknown',
      variant: 'unknown'
    }
  }

  public exit(error?: Error) {
    const { name, variant } = getForcedContentData(this.clientOptions?.forceContent)
    if (error) {
      const reason = getReasonFromError(error)
      monitoringService?.monitorUppLifecycleAborted(reason, name, variant)
    } else {
      monitoringService?.monitorUppLifecycleEnded(name, variant)
    }
    monitoringService?.flush()

    this.destroy(error)
  }

  public destroy(error?: Error): void {
    try {
      this.childConnection?.destroy()
      this.iframe?.remove()
      // eslint-disable-next-line no-empty
    } catch (e) {}
    this.childConnection = null
    this.iframe = null
    this.clientOptions = null
    this.contentOptions = null

    if (error) {
      this.lifeCyclePromiseResolver?.reject(error)
    } else {
      this.lifeCyclePromiseResolver?.resolve()
    }

  }

}
